import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { Archive, Eye, AlertCircle } from "lucide-react";
import { formatWithTimezoneLabel } from "@/lib/timezone";

interface InactiveRFP {
  id: string;
  rfp_id: string;
  invite_token: string;
  hasProposal: boolean;
  rfps: {
    title: string;
    description: string;
    submission_deadline: string;
    auction_start: string;
    status: string;
    matter_name: string | null;
    region: string | null;
    team: string | null;
  };
}

// Helper to capitalize words properly
const capitalizeWords = (str: string) => {
  return str.replace(/_/g, " ").replace(/\b\w/g, char => char.toUpperCase());
};

const SupplierInactiveRFPs = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [invitations, setInvitations] = useState<InactiveRFP[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!authLoading && user) {
      fetchInactiveRFPs();
    } else if (!authLoading && !user) {
      setLoading(false);
    }
  }, [user, authLoading]);

  const fetchInactiveRFPs = async () => {
    if (!user?.email) {
      setLoading(false);
      return;
    }

    try {
      // Fetch invitations where the RFP has been deleted by buyer
      const { data: allInvites, error: invitesError } = await supabase
        .from("supplier_invitations")
        .select(`
          id,
          rfp_id,
          invite_token,
          rfps!inner (
            title,
            description,
            submission_deadline,
            auction_start,
            status,
            matter_name,
            region,
            team,
            deleted_by_buyer
          )
        `)
        .eq("supplier_email", user.email);

      if (invitesError) throw invitesError;

      // Get all proposal RFP IDs for this user
      const { data: userProposals } = await supabase
        .from("proposals")
        .select("rfp_id")
        .eq("user_id", user.id);
      
      const proposalRfpIds = new Set((userProposals || []).map((p: any) => p.rfp_id));

      // Filter for inactive RFPs (deleted_by_buyer = true)
      const inactiveInvites = (allInvites || []).filter((inv: any) => {
        return inv.rfps?.deleted_by_buyer === true;
      }).map((inv: any) => ({
        ...inv,
        hasProposal: proposalRfpIds.has(inv.rfp_id)
      }));
      
      setInvitations(inactiveInvites);
    } catch (err) {
      console.error("Error fetching inactive RFPs:", err);
    } finally {
      setLoading(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-7xl mx-auto space-y-8">
        <div>
          <h1 className="text-3xl font-bold text-foreground flex items-center gap-3">
            <Archive className="h-8 w-8" />
            Inactive
          </h1>
          <p className="text-muted-foreground mt-2">RFPs that have been withdrawn or closed by the client</p>
        </div>

        {invitations.length === 0 ? (
          <Card>
            <CardContent className="pt-6 text-center text-muted-foreground">
              <AlertCircle className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No inactive RFPs</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {invitations.map((inv) => (
              <Card key={inv.id} className="hover:shadow-lg transition-shadow flex flex-col opacity-75">
                <CardHeader>
                  <CardTitle className="text-lg">{inv.rfps.matter_name || inv.rfps.title}</CardTitle>
                  <div className="flex gap-2 flex-wrap">
                    <Badge variant="secondary" className="bg-gray-200 text-gray-600">Withdrawn</Badge>
                    {inv.hasProposal && (
                      <Badge variant="outline">Had Proposal</Badge>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="space-y-4 flex-1 flex flex-col">
                  <p className="text-sm text-muted-foreground line-clamp-2 flex-1">
                    {inv.rfps.description}
                  </p>
                  {(inv.rfps.region || inv.rfps.team) && (
                    <div className="flex flex-wrap gap-2 text-xs">
                      {inv.rfps.region && (
                        <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded">
                          {capitalizeWords(inv.rfps.region)}
                        </span>
                      )}
                      {inv.rfps.team && (
                        <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded">
                          {capitalizeWords(inv.rfps.team)}
                        </span>
                      )}
                    </div>
                  )}
                  <div className="text-sm text-muted-foreground">
                    Originally due: {formatWithTimezoneLabel(inv.rfps.submission_deadline)}
                  </div>
                  <div className="mt-auto pt-4">
                    <Button
                      onClick={() => navigate(`/supplier/rfp/${inv.rfp_id}/view/${inv.invite_token}`)}
                      variant="outline"
                      className="w-full"
                    >
                      <Eye className="h-4 w-4 mr-2" />
                      View Details
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SupplierInactiveRFPs;
